import { ComponentType } from "react";
import {
  APP_AUTH,
  APP_CHANGE_PASSWORD,
  APP_FORGOT_PASSWORD,
  APP_FORGOT_PASSWORD_OTP_VERIFY,
  APP_LOGIN,
  APP_PIN_ENTER,
  APP_SET_PIN,
  APP_CHANGE_CURRENT_PASSWORD,
} from "@/AppConfig";
import { lazyLoad, preloadComponent } from "@/utils/lazyLoad";

/*
 * The sign-in screens load on demand, so signed-in users never download them (or their form libraries).
 * When the app opens on a sign-in address, the forms are fetched together with the page frame and the
 * frame waits for them, so the page appears whole instead of the form popping in and moving the logo.
 */

const forms = {
  LoginForm: () => import("@/views/login/forms/loginForm"),
  SetPin: () => import("@/views/login/forms/setPin"),
  ChangeCurrentPassword: () => import("@/views/login/forms/ChangeCurrentPassword"),
  EnterPin: () => import("@/views/login/forms/enterPin"),
  ForgotPassword: () => import("@/views/login/forms/forgotPassword"),
  VerifyOTP: () => import("@/views/login/forms/VerifyOTP"),
  ChangePassword: () => import("@/views/login/forms/ChangePasssword"),
};
type FormKey = keyof typeof forms;

const loaded: Partial<Record<FormKey, ComponentType>> = {};
const preloadForms = () =>
  Promise.all(
    (Object.keys(forms) as FormKey[]).map((k) =>
      preloadComponent(forms[k]).then((m) => {
        loaded[k] = m.default;
      })
    )
  );
const openedOnSignIn = window.location.pathname.startsWith(APP_AUTH);
// a failed preload never blocks the page: the form then loads on demand
const formsReady = openedOnSignIn
  ? preloadForms().catch(() => undefined)
  : Promise.resolve();

/** renders the form directly once fetched; otherwise loads it on demand */
const authForm = (key: FormKey) => {
  const Lazy = lazyLoad(forms[key]);
  const AuthForm = () => {
    const Form = loaded[key];
    return Form ? <Form /> : <Lazy />;
  };
  return AuthForm;
};

const LoginLayout = lazyLoad(() =>
  Promise.all([import("@/views/login"), formsReady]).then(([m]) => m)
);
const LoginForm = authForm("LoginForm");
const SetPin = authForm("SetPin");
const ChangeCurrentPassword = authForm("ChangeCurrentPassword");
const EnterPin = authForm("EnterPin");
const ForgotPassword = authForm("ForgotPassword");
const VerifyOTP = authForm("VerifyOTP");
const ChangePassword = authForm("ChangePassword");

const AuthRoutes = {
  path: "/",
  element: <LoginLayout />,
  children: [
    {
      path: APP_LOGIN,
      element: <LoginForm />,
    },
    {
      path: APP_SET_PIN,
      element: <SetPin />,
    },
    {
      path: APP_CHANGE_CURRENT_PASSWORD,
      element: <ChangeCurrentPassword />,
    },
    {
      path: APP_PIN_ENTER,
      element: <EnterPin />,
    },
    {
      path: APP_FORGOT_PASSWORD,
      element: <ForgotPassword />,
    },
    {
      path: APP_FORGOT_PASSWORD_OTP_VERIFY,
      element: <VerifyOTP />,
    },
    {
      path: APP_CHANGE_PASSWORD,
      element: <ChangePassword />,
    },
    {
      path: "*",
      element: <LoginForm />,
    },
  ],
};

export default AuthRoutes;
